/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiStatus } from '../../../../ts';
import { EnvironmentType } from '../../../../ts/assistant';
import { Release } from '../../../../ts/release';
import { SET_ASSISTANT_ID } from '../session/actionTypes';
import {
  deleteRelease,
  deployRelease,
  createRelease,
  getRelease,
  listReleasesWithArgs
} from './actions';
import { entityAdapter } from './adapter';

// Initial State
const initialState = entityAdapter.getInitialState({
  listStatus: ApiStatus.IDLE as ApiStatus,
  listError: null,
  listFetched: null,
  deleteStatus: ApiStatus.IDLE as ApiStatus,
  deleteError: null,
  createStatus: ApiStatus.IDLE as ApiStatus,
  createError: null,
  deployStatus: ApiStatus.IDLE as ApiStatus,
  deployError: null,
  getStatus: ApiStatus.IDLE as ApiStatus,
  getError: null,
  assistantId: null,
  isPublishing: false
});

/**
 * Gets the initial state of the redux slice and apply updates.
 * @param {*} updates The updates to apply to the initial state
 * @returns {*} The slice initial state
 */
export const computeState = (updates?: any) => ({
  ..._.cloneDeep(initialState),
  ...updates
});

const markLive = (releases: Array<Release>): Array<Release> => {
  const markedReleases = releases.map(release => {
    if (
      release.environment_references?.some(
        ref => ref.environment === EnvironmentType.LIVE
      )
    ) {
      release.is_live = true;
    } else {
      release.is_live = false;
    }
    return release;
  });
  return markedReleases;
};

// Slice + Reducer
const slice = createSlice({
  name: 'releases',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(listReleasesWithArgs.pending, (state, _action) => {
        state.listStatus = ApiStatus.LOADING;
        state.listError = null;
      })
      .addCase(listReleasesWithArgs.fulfilled, (state, action) => {
        state.listStatus = ApiStatus.SUCCEEDED;
        state.listError = null;
        state.listFetched = Date.now();
        state.assistantId = action.payload.assistantId;
        entityAdapter.setAll(state, markLive(action.payload.releases));
      })
      .addCase(listReleasesWithArgs.rejected, (state, action) => {
        state.listStatus = ApiStatus.ERROR;
        state.listError = action.error;
      })
      .addCase(createRelease.pending, (state, _action) => {
        state.createStatus = ApiStatus.LOADING;
        state.createError = null;
      })
      .addCase(createRelease.fulfilled, (state, action) => {
        state.createStatus = ApiStatus.SUCCEEDED;
        state.createError = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(createRelease.rejected, (state, action) => {
        state.createStatus = ApiStatus.ERROR;
        state.createError = action.error;
      })
      .addCase(deployRelease.pending, (state, _action) => {
        state.deployStatus = ApiStatus.LOADING;
        state.deployError = null;
      })
      .addCase(deployRelease.fulfilled, (state, _action) => {
        state.deployStatus = ApiStatus.SUCCEEDED;
        state.deployError = null;
      })
      .addCase(deployRelease.rejected, (state, action) => {
        state.deployStatus = ApiStatus.ERROR;
        state.deployError = action.error;
      })
      .addCase(deleteRelease.pending, (state, _action) => {
        state.deleteStatus = ApiStatus.LOADING;
        state.deleteError = null;
      })
      .addCase(deleteRelease.fulfilled, (state, action) => {
        state.deleteStatus = ApiStatus.SUCCEEDED;
        state.deleteError = null;
        entityAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteRelease.rejected, (state, action) => {
        state.deleteStatus = ApiStatus.ERROR;
        state.deleteError = action.error;
      })
      .addCase(getRelease.fulfilled, (state, action) => {
        state.getStatus = ApiStatus.SUCCEEDED;
        state.getError = null;
        entityAdapter.upsertOne(state, action.payload);
      })
      .addCase(getRelease.pending, (state, _action) => {
        state.getStatus = ApiStatus.LOADING;
        state.getError = null;
      })
      .addCase(getRelease.rejected, (state, action) => {
        state.getStatus = ApiStatus.ERROR;
        state.getError = action.error;
      })
      .addCase<string, AnyAction>(SET_ASSISTANT_ID, (_state, action) => {
        if (action.assistantId) {
          return computeState({
            assistantId: action.assistantId
          });
        }
      });
  }
});

export default slice.reducer;
